import type { AxiosResponse } from "axios";
import type { RefObject } from "react";
import type { Provider } from "@blueskydataplatform/shared-ui/types/providers.types";
import { paths } from "@/config/paths";
import { DEFAULT_MCP_PERMISSIONS } from "@/providers/mcp-default-permissions";
import type { McpCallbackHandle } from "./mcp-callback-form";

export type McpPermissions = {
  include?: string[];
  exclude?: string[];
};

type SoftwareStatementResponse = {
  software_statement: string;
};

type FetchSoftwareStatement = (input: {
  permissions: McpPermissions;
}) => Promise<AxiosResponse<SoftwareStatementResponse>>;

type CompleteMcpAuthCallbackParams = {
  permissions?: McpPermissions | null;
  fetchSoftwareStatement: FetchSoftwareStatement;
  callbackRef: RefObject<McpCallbackHandle | null>;
  /** The screen's own button is the Allow: it asks the same question and shows McpAccessDestination. */
  alreadyConsented?: boolean;
};

const PROVIDER_RESOLVE_TIMEOUT_MS = 15_000;
const PROVIDER_RESOLVE_POLL_MS = 50;

/**
 * Waits until InitProvider has resolved a non-null MCP provider.
 * Without this, callers that race a fast login against a slow provider fetch
 * would pass `undefined` permissions and fall through to DEFAULT_MCP_PERMISSIONS.
 */
export async function waitForResolvedProvider(
  getProvider: () => Provider | null | undefined,
  timeoutMs = PROVIDER_RESOLVE_TIMEOUT_MS
): Promise<Provider> {
  const startedAt = Date.now();
  while (true) {
    const provider = getProvider();
    if (provider) {
      return provider;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      throw new McpCallbackError(MCP_PROVIDER_ERROR);
    }
    await new Promise(resolve => setTimeout(resolve, PROVIDER_RESOLVE_POLL_MS));
  }
}

/**
 * Asks the visitor to allow the connection, then fetches a software statement and POSTs it
 * to the MCP auth callback form.
 * Shared by fresh login, 2FA, already-signed-in MCP, and finish-registration flows.
 */
export async function completeMcpAuthCallback({
  permissions,
  fetchSoftwareStatement,
  callbackRef,
  alreadyConsented = false,
}: CompleteMcpAuthCallbackParams): Promise<void> {
  // Checked before the statement is minted, not after: McpCallbackForm exposes no handle
  // when there is no callback URL to post to, and a statement we can't deliver is a live
  // credential issued for nobody. Previously this fell through to a form whose empty
  // action posts back to this very page, which looks like nothing happening at all.
  const callback = callbackRef.current;
  if (!callback) {
    throw new McpCallbackError(MCP_CALLBACK_URL_ERROR);
  }
  // Also before minting: a cancelled connection never creates a credential.
  if (!alreadyConsented && !(await callback.askConsent())) {
    throw new McpCallbackError(MCP_CANCELLED_ERROR);
  }

  const resolvedPermissions = permissions ?? DEFAULT_MCP_PERMISSIONS;
  const softwareStatementResponse = await fetchSoftwareStatement({
    permissions: resolvedPermissions,
  });
  const softwareStatement = softwareStatementResponse.data?.software_statement || "";
  if (!softwareStatement) {
    throw new Error("Software statement not found");
  }
  callback.submit(softwareStatement);
}

/**
 * Resolves the MCP provider first, then completes the auth callback with that
 * provider's permissions (never with a pre-resolve `undefined` fallback).
 */
export async function completeMcpAuthCallbackForProvider({
  getProvider,
  fetchSoftwareStatement,
  callbackRef,
}: {
  getProvider: () => Provider | null | undefined;
  fetchSoftwareStatement: FetchSoftwareStatement;
  callbackRef: RefObject<McpCallbackHandle | null>;
}): Promise<void> {
  const provider = await waitForResolvedProvider(getProvider);
  await completeMcpAuthCallback({
    permissions: provider.mcp?.permissions,
    fetchSoftwareStatement,
    callbackRef,
  });
}

/**
 * A failure we can name for the visitor. Anything else is an unlabelled bug on our side
 * and gets the generic message, so the copy never asserts a cause we haven't established.
 */
export class McpCallbackError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "McpCallbackError";
  }
}

/** Reached only when the org has no usable API key — the one case verification explains. */
export const MCP_SOFTWARE_STATEMENT_ERROR =
  "We couldn’t retrieve your software statement. Please check that your email address has been verified.";

/** The link is missing `callback_url`, so there is nowhere to send the statement. */
export const MCP_CALLBACK_URL_ERROR = "This connection link is incomplete.";

/** The visitor pressed Cancel on the consent dialog; nothing was shared. */
export const MCP_CANCELLED_ERROR = "You cancelled the connection. Nothing was shared.";

/** Our own provider fetch didn't finish; retrying usually works. */
export const MCP_PROVIDER_ERROR = "We couldn’t load your connection settings in time.";

/** The session lapsed between arriving here and completing the handshake. */
export const MCP_SESSION_ERROR = "Your session ended before the connection finished. Sign in again to continue.";

/**
 * The failure the visitor should be told about. Everything used to say "check that your
 * email address has been verified", which is right for exactly one of these and actively
 * misleading for the rest — people re-verify a perfectly verified address and try again.
 */
export const getMcpFailureMessage = (error: unknown): string => {
  if (error instanceof McpCallbackError) {
    return error.message;
  }
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 ? MCP_SESSION_ERROR : MCP_SOFTWARE_STATEMENT_ERROR;
};

/** Retrying a link that never had a callback URL just fails again in the same place. */
export const isMcpFailureRetryable = (error: unknown): boolean =>
  !(error instanceof McpCallbackError && error.message === MCP_CALLBACK_URL_ERROR);

/**
 * /software-statement forwards the api-service status. 402 is the only one the visitor
 * can act on: the org's trial or subscription has ended, so the connection fails until
 * somebody buys a plan. Everything else is ours to fix, not theirs.
 *
 * Sends them to a page that says so and offers the marketplace, carrying the MCP query
 * string along so the page can name the client they came from. Returns true once it has
 * navigated, so the caller knows not to also raise a toast nobody will be around to read.
 */
export const redirectOnLapsedEntitlement = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status !== 402) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  params.set("reason", "entitlement");
  window.location.assign(`${paths.base}${paths.subscriptionRequired.getHref()}?${params.toString()}`);
  return true;
};

/**
 * What the three screens with a visible page of their own do on failure: log it, hand a
 * lapsed plan to the page that explains it, and otherwise say which of our problems this
 * was. login.tsx renders an error state instead — there is no page behind its toast.
 */
export const reportMcpFailure = (error: unknown, showError: (message: string) => void): void => {
  console.error(error);
  if (redirectOnLapsedEntitlement(error)) {
    return;
  }
  showError(getMcpFailureMessage(error));
};
